import { WizebulkLogoIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import Avatar from "../../shared/components/Avatar";
import WithTooltip from "../../shared/components/WithTooltip";
import { capitalizeName } from "../../shared/services/utils";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type Item = {
  VOYAGE_NO: number;
  VOYAGE_STATUS_CODE: string;
  OPR_TYPE: string;
  COMMENCE_DATE_GMT: string; // ISO date string
  COMPLETED_DATE_GMT: string; // ISO date string
  LOAD_PORT?: string;
  DISCHARGE_PORT: string;
  CHARTERER: string;
  CARGO: string;
  CP_QUANTITY: number;
  OPS_COORDINATOR?: string;
  CHTR_SPECIALIST?: string;
  TC_CODE?: string;
};

interface Props {
  item: Item;
}

const VoyageDetailRow = ({ title, value }: { title: string; value: string | number }) => (
  <div className="flex min-h-5 items-center gap-2 text-xs">
    <span className="w-1/2">{title}</span>
    <span className="w-1/2 font-bold">{value}</span>
  </div>
);

const Divider = () => {
  const theme = useAtomValue(themeAtom);

  return <div className={twMerge("h-px w-full", theme.vesselDetails.divider)} />;
};

const VerticalDivider = () => {
  const theme = useAtomValue(themeAtom);
  return <div className={twMerge("w-px self-stretch", theme.vesselDetails.divider)} />;
};

const InfoBadge = ({ text, item }: { text: string; item: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-gray-400">{text}</span>
    {item}
  </div>
);

export function VoyageCard({ item }: Props) {
  const theme = useAtomValue(themeAtom);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <Link
      className={twMerge(
        "mb-4 flex cursor-pointer flex-col gap-2.5 rounded border p-4",
        theme.home.tailwind.textDefault,
        theme.uwhcAssistant.border,
      )}
      to={`/voyage/details/${item.TC_CODE}/${item.VOYAGE_NO}`}
    >
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <span className="text-greyscale-100 text-sm font-bold">Voyage #{item.VOYAGE_NO}</span>
        <span className={twMerge("rounded-full bg-green px-2 py-1 text-xs font-medium", theme.warningFunction.text)}>
          {item.VOYAGE_STATUS_CODE}
        </span>
      </div>

      {/* Voyage Details */}
      <div className={twMerge("flex flex-col gap-2 rounded p-4", theme.myVesselsTableRow.row.primaryMedium)}>
        <span
          className={twMerge(
            "self-start rounded-full border border-gray-400 px-2 py-1 text-xs font-medium text-gray-400",
            theme.headerComponent.tailwind.backButtonBg,
          )}
        >
          {item.OPR_TYPE}
        </span>
        <div className="flex flex-col gap-1">
          <VoyageDetailRow title="Commence date" value={formatDate(item.COMMENCE_DATE_GMT)} />
          <VoyageDetailRow title="Complete date" value={formatDate(item.COMPLETED_DATE_GMT)} />
          {item.LOAD_PORT && <VoyageDetailRow title="Loading Port" value={item.LOAD_PORT} />}
          <VoyageDetailRow title="Discharging Port" value={item.DISCHARGE_PORT} />
        </div>
      </div>

      {/* Charterer and Cargo Information */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <WizebulkLogoIcon className={twMerge("ml-2 h-8 w-8", theme.home.tailwind.textDefault)} />
          <WithTooltip id="tooltip-charterer" content={item.CHARTERER}>
            <span className="truncate text-xs">{item.CHARTERER}</span>
          </WithTooltip>
        </div>
        <VerticalDivider />
        <span className="text-xs">{item.CARGO}</span>
        <VerticalDivider />
        <span className="text-xs">{item.CP_QUANTITY}</span>
      </div>

      {/* Divider */}
      <Divider />

      {/* Avatars */}
      <div className="flex gap-4">
        <InfoBadge text="Ops Coordinator" item={<Avatar name={capitalizeName(item.OPS_COORDINATOR ?? "")} />} />
        <InfoBadge text="Trader" item={<Avatar name={capitalizeName(item.CHTR_SPECIALIST ?? "")} />} />
      </div>
    </Link>
  );
}
